<div class="machining">
  <div class="container-fluid pt-4">

    <!-- Overview view -->
    <ng-container *ngIf="currentView === 'overview'">
      <h1 class="page-title text-center">Machine Shop</h1>
      <p class="page-subtitle text-center">Manual lathe and mill — your learning log.</p>

      <div class="track-toggle">
        <button
          class="track-btn"
          [class.active]="selectedTrack === 'lathe'"
          (click)="selectTrack('lathe')">
          Lathe
        </button>
        <button
          class="track-btn"
          [class.active]="selectedTrack === 'mill'"
          (click)="selectTrack('mill')">
          Mill
        </button>
      </div>

      <div class="track-progress">
        <div class="track-progress-label">{{ trackProgressLabel }}</div>
        <div class="track-progress-bar">
          <div class="track-progress-fill" [style.width.%]="trackProgressPercent"></div>
        </div>
      </div>

      <div class="module-grid">
        <button
          *ngFor="let module of trackModules"
          class="module-card"
          [class.locked]="getModuleStatus(module) === 'locked'"
          [class.completed]="getModuleStatus(module) === 'completed'"
          [class.visited]="getModuleStatus(module) === 'visited'"
          [disabled]="getModuleStatus(module) === 'locked'"
          (click)="openModule(module)">
          <div class="module-card-head">
            <span class="module-number">#{{ module.number }}</span>
            <span class="module-icon">{{ module.icon }}</span>
            <span class="module-status-pill" [ngSwitch]="getModuleStatus(module)">
              <span *ngSwitchCase="'locked'">🔒</span>
              <span *ngSwitchCase="'completed'">✓</span>
              <span *ngSwitchCase="'visited'">●</span>
              <span *ngSwitchCase="'available'">○</span>
            </span>
          </div>
          <div class="module-title">{{ module.title }}</div>
          <div class="module-subtitle">{{ module.subtitle }}</div>
          <div class="module-time">⏱ {{ module.estimatedTime }}</div>
        </button>
      </div>

      <div class="overview-links">
        <a routerLink="/machining/journal" class="overview-link">📓 Session journal</a>
        <a routerLink="/machining/my-gallery" class="overview-link">🖼 My gallery</a>
      </div>
    </ng-container>

    <!-- Module view -->
    <ng-container *ngIf="currentView === 'module' && selectedModule">
      <button class="back-btn" (click)="goToOverview()">← All modules</button>

      <div class="module-head">
        <div class="module-head-icon">{{ selectedModule.icon }}</div>
        <div>
          <h1 class="module-page-title">{{ selectedModule.title }}</h1>
          <p class="module-page-subtitle">{{ selectedModule.subtitle }}</p>
          <div class="module-meta">
            <span class="meta-pill">⏱ {{ selectedModule.estimatedTime }}</span>
            <span class="meta-pill machine-pill"
                  [class.lathe]="selectedModule.machine === 'lathe'"
                  [class.mill]="selectedModule.machine === 'mill'"
                  [class.both]="!selectedModule.machine">
              {{ machinePillLabel(selectedModule.machine) }}
            </span>
          </div>
        </div>
      </div>

      <div class="module-empty"
           *ngIf="selectedModule.sections.length === 0
                  && !selectedModule.toolsNeeded?.length
                  && !selectedModule.preChecklist?.length
                  && !selectedModule.practiceExercises?.length
                  && !selectedModule.commonMistakes?.length">
        <p>This module's content is being authored. Check back soon.</p>
      </div>

      <!-- Block 1: Why this matters (description as lead paragraph) -->
      <p *ngIf="selectedModule.description" class="why-this-matters">
        {{ selectedModule.description }}
      </p>

      <!-- Block 2: Tools you'll need -->
      <section *ngIf="selectedModule.toolsNeeded?.length" class="tools-block">
        <h2 class="block-title">🛠 Tools you'll need</h2>
        <ul class="tools-list">
          <li *ngFor="let tool of selectedModule.toolsNeeded">{{ tool }}</li>
        </ul>
      </section>

      <!-- Block 3: Concept (sections + callouts) -->
      <section *ngFor="let section of selectedModule.sections" class="lesson-section">
        <h2 class="lesson-section-title">{{ section.title }}</h2>
        <div class="lesson-section-body" [innerHTML]="section.content | safeHtml"></div>
        <div *ngIf="section.callout" class="callout" [class]="'callout-' + section.callout.type">
          <div class="callout-title">{{ section.callout.title }}</div>
          <div class="callout-text">{{ section.callout.text }}</div>
        </div>
      </section>

      <!-- Block 4: Demonstration videos -->
      <section *ngIf="selectedModule.videos.length" class="videos-section">
        <h2 class="videos-title">Watch &amp; learn</h2>
        <div class="video-list">
          <button
            *ngFor="let video of selectedModule.videos"
            class="video-card"
            (click)="openVideo(video.youtubeId, video.title, video.searchQuery)">
            <div class="video-card-title">{{ video.title }}</div>
            <div class="video-card-channel">{{ video.channel }}</div>
            <div class="video-card-desc">{{ video.description }}</div>
          </button>
        </div>
      </section>

      <!-- Block 5: Pre-flight checklist -->
      <section *ngIf="selectedModule.preChecklist?.length" class="prechecklist-block">
        <h2 class="block-title">✅ Before the spindle spins</h2>
        <ul class="prechecklist-list">
          <li *ngFor="let item of selectedModule.preChecklist">{{ item }}</li>
        </ul>
      </section>

      <!-- Block 6: Practice exercises -->
      <section *ngIf="selectedModule.practiceExercises?.length" class="practice-block">
        <div class="practice-head">
          <h2 class="block-title">🎯 Practice</h2>
          <span class="practice-count">{{ exercisesCompleteCount }} / {{ selectedModule.practiceExercises?.length }} complete</span>
        </div>
        <div class="practice-progress-bar">
          <div class="practice-progress-fill"
               [style.width.%]="(exercisesCompleteCount / (selectedModule.practiceExercises?.length || 1)) * 100"></div>
        </div>

        <div *ngFor="let ex of selectedModule.practiceExercises; let i = index"
             class="exercise-card"
             [class.completed]="isExerciseComplete(ex.id)">
          <button type="button"
                  class="exercise-toggle"
                  (click)="toggleExercise(ex.id)"
                  [attr.aria-pressed]="isExerciseComplete(ex.id)"
                  [attr.aria-label]="isExerciseComplete(ex.id) ? 'Mark as incomplete' : 'Mark as complete'">
            <span class="exercise-toggle-mark" *ngIf="isExerciseComplete(ex.id)">✓</span>
          </button>

          <div class="exercise-body">
            <div class="exercise-header">
              <span class="exercise-number">{{ exerciseNumberLabel(i) }}</span>
              <span class="exercise-description" [innerHTML]="ex.description | safeHtml"></span>
            </div>

            <div *ngIf="ex.hint" class="exercise-hint">💡 {{ ex.hint }}</div>

            <div *ngIf="isExerciseComplete(ex.id)" class="exercise-done-badge">
              ✓ Completed {{ formatCompletedDate(getExerciseProgress(ex.id)?.completedAt) }}
            </div>

            <button *ngIf="!isNotesExpanded(ex.id)"
                    type="button"
                    class="exercise-notes-toggle"
                    (click)="toggleNotesExpanded(ex.id)">
              + Add notes
            </button>
            <textarea
              *ngIf="isNotesExpanded(ex.id)"
              class="exercise-notes"
              placeholder="What did you observe? What surprised you?"
              [value]="notesDrafts[ex.id] || ''"
              (input)="onExerciseNotesChange(ex.id, $any($event.target).value)"
              (blur)="onExerciseNotesBlur(ex.id)"
              rows="3"></textarea>
          </div>
        </div>
      </section>

      <!-- Block 7: Common mistakes -->
      <section *ngIf="selectedModule.commonMistakes?.length" class="mistakes-block">
        <h2 class="block-title">⚠️ Common mistakes</h2>
        <ul class="mistakes-list">
          <li *ngFor="let m of selectedModule.commonMistakes">{{ m }}</li>
        </ul>
      </section>

      <!-- Block 8: Quiz CTA -->
      <section *ngIf="selectedModule.quiz.length" class="quiz-cta-card">
        <div class="quiz-cta-body">
          <div class="quiz-cta-icon">📝</div>
          <div class="quiz-cta-text">
            <h2 class="quiz-cta-title">Self-check quiz</h2>
            <p class="quiz-cta-meta">
              {{ selectedModule.quiz.length }} question{{ selectedModule.quiz.length === 1 ? '' : 's' }}
              <ng-container *ngIf="getModuleProgress(selectedModule.id)?.quizScore != null">
                · Best score: <strong>{{ getModuleProgress(selectedModule.id)?.quizScore }}%</strong>
              </ng-container>
            </p>
          </div>
        </div>
        <button class="btn-quiz" (click)="startQuiz()">
          {{ getModuleProgress(selectedModule.id)?.quizCompleted ? 'Retry quiz' : 'Take the quiz' }} →
        </button>
      </section>

      <!-- Block 9: Sources -->
      <section *ngIf="selectedModule.sources.length" class="sources-section">
        <h2 class="sources-title">Sources &amp; further reading</h2>
        <ul class="sources-list">
          <li *ngFor="let s of selectedModule.sources">
            <a [href]="s.url" target="_blank" rel="noopener">{{ s.label }}</a>
          </li>
        </ul>
      </section>
    </ng-container>

    <!-- Video lightbox -->
    <div *ngIf="activeVideoId" class="video-modal-backdrop" (click)="closeVideo()">
      <div class="video-modal" (click)="$event.stopPropagation()">
        <div class="video-modal-head">
          <span>{{ activeVideoTitle }}</span>
          <button (click)="closeVideo()">✕</button>
        </div>
        <div class="video-frame">
          <iframe
            [src]="('https://www.youtube.com/embed/' + activeVideoId) | safeUrl"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen></iframe>
        </div>
      </div>
    </div>

    <!-- Quiz view -->
    <ng-container *ngIf="currentView === 'quiz' && selectedModule">
      <button class="back-btn" (click)="backToModule()">← Back to module</button>

      <div *ngIf="!quizCompleted && currentQuestion" class="quiz-card">
        <div class="quiz-progress">
          Question {{ currentQuestionIndex + 1 }} of {{ selectedModule.quiz.length }}
        </div>
        <h2 class="quiz-question">{{ currentQuestion.question }}</h2>

        <div class="quiz-options">
          <button
            *ngFor="let opt of currentQuestion.options; let i = index"
            class="quiz-option"
            [class.selected]="selectedAnswerIndex === i && !showExplanation"
            [class.correct]="showExplanation && i === currentQuestion.correctIndex"
            [class.wrong]="showExplanation && selectedAnswerIndex === i && i !== currentQuestion.correctIndex"
            [disabled]="showExplanation"
            (click)="selectAnswer(i)">
            {{ opt }}
          </button>
        </div>

        <div *ngIf="showExplanation" class="quiz-explanation"
             [class.correct-bg]="isCurrentAnswerCorrect"
             [class.wrong-bg]="!isCurrentAnswerCorrect">
          <strong>{{ isCurrentAnswerCorrect ? 'Correct.' : 'Not quite.' }}</strong>
          {{ currentQuestion.explanation }}
        </div>

        <div class="quiz-actions">
          <button
            *ngIf="!showExplanation"
            class="btn-quiz-action"
            [disabled]="selectedAnswerIndex === null"
            (click)="submitAnswer()">
            Submit
          </button>
          <button
            *ngIf="showExplanation"
            class="btn-quiz-action"
            (click)="nextQuestion()">
            {{ currentQuestionIndex < selectedModule.quiz.length - 1 ? 'Next →' : 'Finish' }}
          </button>
        </div>
      </div>

      <div *ngIf="quizCompleted" class="quiz-result" [class]="getScoreClass(quizScore)">
        <div class="quiz-score">{{ quizScore }}%</div>
        <p class="quiz-score-msg">{{ getScoreMessage(quizScore) }}</p>
        <div class="quiz-result-actions">
          <button class="btn-quiz-action secondary" (click)="startQuiz()">Retry</button>
          <button class="btn-quiz-action" (click)="backToModule()">Back to module</button>
        </div>
      </div>
    </ng-container>

  </div>
</div>
